import { FolderOpen } from "lucide-react";
// src/pages/super-admin/layout/SuperAdminLayout.tsx
import React from "react";
import { Outlet, NavLink, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import NotificationBell from "@/components/notifications/NotificationBell";
import { Home, Users, CreditCard, Send, Settings, LogOut, TrendingUp } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { clearAccessToken } from "@/api/client";
import { useLanguage } from "@/contexts/LanguageContext";

const PRIMARY = "#1F3A93"; // brand blue
const BG = "#F5F6FA";

export default function SuperAdminLayout() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { t } = useLanguage();

  async function handleLogout() {
    try {
      // Call logout (backend + clear tokens + clear sessionStorage)
      await logout();
      
      // Clear sessionStorage (Super Admin uses sessionStorage, NOT localStorage)
      sessionStorage.clear();
      
      // Clear in-memory token
      clearAccessToken();
      
      // Force full page reload to clear browser cache and prevent race conditions
      window.location.href = "/#/login";
    } catch (error) {
      console.error("Logout failed:", error);
      
      // Force logout even if everything fails
      sessionStorage.clear();
      clearAccessToken();
      
      // Force reload
      window.location.href = "/#/login";
    }
  }

  return (
    <div className="min-h-screen flex flex-col relative" style={{ backgroundColor: BG }}>
      {/* Ambient background */}
      <div className="pointer-events-none absolute inset-0 -z-10 bg-[radial-gradient(1000px_600px_at_50%_-10%,rgba(31,58,147,0.06),transparent_60%)]" />

      {/* Header (glass) — matches ResidentLayout */}
      <header className="sticky top-0 z-40">
        <div className="border-b border-black/5 bg-white/70 backdrop-blur-xl">
          <div className="max-w-5xl mx-auto px-4 py-2.5">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <h1 className="text-[17px] font-semibold leading-tight tracking-[-0.01em]">
                  {titleFromPath(pathname, t)}
                </h1>
                <div className="flex items-center gap-2 text-[13px] text-gray-500">
                  <span>{t("superAdmin.layout.console")}</span>
                  <span>•</span>
                  <span>{t("roles.superAdmin")}</span>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <NotificationBell role="super-admin" />
                <Button
                  size="sm"
                  onClick={handleLogout}
                  title={t("common.logout")}
                  className="gap-2 rounded-xl bg-white text-gray-900 ring-1 ring-black/5 border-0 hover:bg-white active:bg-gray-50"
                >
                  <LogOut className="w-4 h-4" />
                  <span className="hidden sm:inline">{t("common.logout")}</span>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main */}
      <main className="flex-1">
        <div className="max-w-5xl mx-auto px-4 py-4 pb-28 sm:pb-32">
          <Outlet />
        </div>
      </main>

      {/* Floating bottom dock (glass) — matches ResidentLayout */}
      <nav className="fixed inset-x-0 bottom-0 z-40 pt-5 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
        {/* The dock floats clear of the bottom edge, so the page used to scroll
            through the 12px gap beneath it and past its rounded corners -- cards
            reappeared under the bar and it read as content escaping. The nav now
            spans to the bottom edge and paints the page colour behind itself,
            fading out above so content is not cut off with a hard line. */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={{ background: `linear-gradient(to top, ${BG} 0%, ${BG} 62%, transparent 100%)` }}
        />
        <div className="relative mx-auto max-w-5xl px-1 sm:px-2">
          <ul className="grid grid-cols-6 gap-0.5 sm:gap-1 rounded-2xl bg-white/80 backdrop-blur-xl ring-1 ring-black/5 shadow-lg shadow-black/10 p-1.5 sm:p-2">
            <Tab to="/super-admin" icon={<Home className="w-5 h-5" />} label={t("nav.home")} />
            <Tab to="/super-admin/hoa-admins" icon={<Users className="w-5 h-5" />} label={t("nav.hoas")} />
            <Tab to="/super-admin/payments" icon={<CreditCard className="w-5 h-5" />} label={t("nav.payments")} />
            <Tab to="/super-admin/rates" icon={<TrendingUp className="w-5 h-5" />} label={t("nav.rates")} />
            {/* <Tab to="/super-admin/payouts" icon={<Send className="w-5 h-5" />} label="Payouts" /> */}
            <Tab to="/super-admin/documents" icon={<FolderOpen className="w-5 h-5" />} label={t("m3.documents.title")} />
            <Tab to="/super-admin/settings" icon={<Settings className="w-5 h-5" />} label={t("nav.settings")} />
          </ul>
        </div>
      </nav>
    </div>
  );
}

function Tab({
  to,
  icon,
  label,
}: {
  to: string;
  icon: React.ReactNode;
  label: string;
}) {
  return (
    // min-w-0 is load-bearing: a grid item defaults to min-width:auto, so it
    // refuses to shrink below its content and the label spills across its
    // neighbours instead of being clipped.
    <li className="min-w-0">
      <NavLink
        to={to}
        end={to === "/super-admin"}
        className={({ isActive }: { isActive: boolean }) =>
          [
            "group relative flex w-full min-w-0 flex-col items-center justify-center rounded-xl py-2 transition",
            "focus-visible:ring-2 focus-visible:ring-[color:var(--brand)]/30 outline-none",
          ].join(" ")
        }
      >
        {({ isActive }: { isActive: boolean }) => (
          <>
            <span
              className="grid place-items-center transition-colors"
              style={{ color: isActive ? PRIMARY : "#4b5563" /* gray-600 */ }}
            >
              {icon}
            </span>
            {/* truncate is the backstop: a longer translation clips
                cleanly rather than overlapping the next tab. */}
            <span
              className="mt-1 w-full truncate text-center text-[10px] font-medium tracking-tight transition-colors sm:text-[11px]"
              style={{ color: isActive ? PRIMARY : "#4b5563" }}
              title={label}
            >
              {label}
            </span>

            {/* tiny active indicator */}
            <span
              aria-hidden
              className="absolute -bottom-0.5 h-1 w-6 rounded-full transition-opacity"
              style={{
                backgroundColor: PRIMARY,
                opacity: isActive ? 0.7 : 0,
              }}
            />
          </>
        )}
      </NavLink>
    </li>
  );
}

function titleFromPath(path: string, t: (key: string) => string) {
  if (path.includes("/hoa-admins")) return t("superAdmin.layout.titles.hoaAdmins");
  if (path.includes("/payments")) return t("superAdmin.layout.titles.payments");
  if (path.includes("/rates")) return t("rates.title");
  // if (path.includes("/payouts")) return "Pagos a HOA";
  if (path.includes("/documents")) return t("m3.documents.title");
  if (path.includes("/settings")) return t("nav.settings");
  return t("nav.dashboard");
}
